/**
 * Data Synchronization Service
 * 
 * Core service for managing bidirectional data synchronization between
 * IndexedDB (offline) and Supabase (remote) with conflict resolution,
 * selective loading, and queue management.
 */

import { indexedDBService } from '../indexeddb';
import { syncOperations } from './SyncOperations';
import { selectiveLoader } from './SelectiveLoader';
import { conflictResolver, type Conflict } from './ConflictResolver';

/**
 * Sync configuration options
 */
export interface SyncConfig {
  // Data loading preferences
  journalDays: number; // Default: 90 days
  enableBackgroundSync: boolean; // Default: true
  syncInterval: number; // Default: 5 minutes (300000ms)
  
  // Retry configuration
  maxRetries: number; // Default: 3
  retryDelay: number; // Default: 1000ms (exponential backoff)
  
  // Conflict resolution
  defaultConflictResolution: 'local_wins' | 'remote_wins' | 'manual'; // Default: 'manual'
  
  // Network detection
  enableNetworkDetection: boolean; // Default: true
  
  // Progress tracking
  enableProgressTracking: boolean; // Default: true
}

/**
 * Sync status and progress information
 */
export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: string | null;
  pendingOperations: number;
  failedOperations: number;
  conflictCount: number;
  progress: {
    current: number;
    total: number;
    stage: string;
  } | null;
}

/**
 * Sync event types for the event system
 */
export type SyncEvent = 
  | { type: 'sync_started'; data: { stage: string } }
  | { type: 'sync_progress'; data: { current: number; total: number; stage: string } }
  | { type: 'sync_completed'; data: { duration: number; operations: number } }
  | { type: 'sync_failed'; data: { error: string; stage: string } }
  | { type: 'conflict_detected'; data: { tableName: string; recordId: string } }
  | { type: 'network_status_changed'; data: { isOnline: boolean } };

/**
 * Event listener type
 */
export type SyncEventListener = (event: SyncEvent) => void;

/**
 * Main Synchronization Service
 */
export class SyncService {
  private config: SyncConfig;
  private status: SyncStatus;
  private eventListeners: Set<SyncEventListener> = new Set();
  private syncInterval: NodeJS.Timeout | null = null;
  private isInitialized = false;
  private syncPromise: Promise<void> | null = null; // Track ongoing sync promise

  constructor(config: Partial<SyncConfig> = {}) {
    this.config = {
      journalDays: 90,
      enableBackgroundSync: true,
      syncInterval: 86400000, // 24 hours (1 day)
      maxRetries: 3,
      retryDelay: 1000,
      defaultConflictResolution: 'manual',
      enableNetworkDetection: true,
      enableProgressTracking: true,
      ...config
    };

    this.status = {
      isOnline: navigator.onLine,
      isSyncing: false,
      lastSyncTime: null,
      pendingOperations: 0,
      failedOperations: 0,
      conflictCount: 0,
      progress: null
    };
  }

  /**
   * Initialize the sync service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize IndexedDB service
      await indexedDBService.initialize();

      // Set up network detection
      if (this.config.enableNetworkDetection) {
        this.setupNetworkDetection();
      }

      // Load initial sync status
      await this.loadSyncStatus();

      // Start background sync if enabled
      if (this.config.enableBackgroundSync) {
        this.startBackgroundSync();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize Sync Service:', error);
      throw error;
    }
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    return { ...this.status };
  }

  /**
   * Get sync configuration
   */
  getConfig(): SyncConfig {
    return { ...this.config };
  }

  /**
   * Update sync configuration
   */
  updateConfig(updates: Partial<SyncConfig>): void {
    this.config = { ...this.config, ...updates };
    
    // Restart background sync if interval changed
    if (updates.syncInterval && this.config.enableBackgroundSync) {
      this.stopBackgroundSync();
      this.startBackgroundSync();
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: SyncEventListener): void {
    this.eventListeners.add(listener);

  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: SyncEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit sync event
   */
  private emitEvent(event: SyncEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in sync event listener:', error);
      }
    });
  }

  /**
   * Setup network detection
   */
  private setupNetworkDetection(): void {
    const handleOnline = () => {
      this.status.isOnline = true;
      this.emitEvent({ type: 'network_status_changed', data: { isOnline: true } });

      // Trigger sync when coming back online (with small delay to prevent race conditions)
      if (this.config.enableBackgroundSync) {
        setTimeout(() => {
          this.performSync().catch(error => {
            console.error('Auto-sync failed after coming online:', error);
          });
        }, 1000); // 1 second delay
      }
    };

    const handleOffline = () => {
      this.status.isOnline = false;
      this.emitEvent({ type: 'network_status_changed', data: { isOnline: false } });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  }

  /**
   * Load sync status from storage
   */
  private async loadSyncStatus(): Promise<void> {
    try {
      // Get sync queue statistics
      const queueStats = await indexedDBService.syncQueue.getStats();
      this.status.pendingOperations = queueStats.pending + queueStats.processing;
      this.status.failedOperations = queueStats.failed;

      // Get last sync time from metadata
      const metadata = await indexedDBService.syncMetadata.getAllMetadata();
      if (metadata.length > 0) {
        const lastSync = metadata.reduce((latest, meta) => 
          meta.last_sync > latest ? meta.last_sync : latest, '');
        this.status.lastSyncTime = lastSync || null;
      }

      // TODO: Get conflict count from conflict resolution store
      this.status.conflictCount = 0;
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  }

  /**
   * Start background sync
   */
  private startBackgroundSync(): void {
    if (this.syncInterval) return;

    this.syncInterval = setInterval(async () => {
      if (this.status.isOnline && !this.status.isSyncing) {
        try {
          // Only sync if it's been more than 24 hours since last sync OR there are pending operations
          const shouldSync = await this.shouldPerformBackgroundSync();
          if (shouldSync) {
            console.log('🔄 Performing daily background sync...');
            await this.performSync();
          }
        } catch (error) {
          console.error('Background sync failed:', error);
        }
      }
    }, this.config.syncInterval);
  }

  /**
   * Stop background sync
   */
  private stopBackgroundSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Check if background sync should run
   */
  private async shouldPerformBackgroundSync(): Promise<boolean> {
    try {
      // Check if there are pending operations (always sync if there are)
      const pendingItems = await indexedDBService.syncQueue.getPendingItems();
      if (pendingItems.length > 0) {
        console.log(`🔄 Background sync needed: ${pendingItems.length} pending operations`);
        return true;
      }

      // Check if it's been more than 24 hours since last sync
      if (this.status.lastSyncTime) {
        const lastSync = new Date(this.status.lastSyncTime);
        const now = new Date();
        const hoursSinceLastSync = (now.getTime() - lastSync.getTime()) / (1000 * 60 * 60);

        if (hoursSinceLastSync >= 24) {
          console.log(`🔄 Background sync needed: ${Math.round(hoursSinceLastSync)} hours since last sync`);
          return true;
        }
      } else {
        // No previous sync recorded, should sync
        console.log('🔄 Background sync needed: no previous sync recorded');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking if background sync should run:', error);
      return false;
    }
  }

  /**
   * Perform full synchronization
   */
  async performSync(): Promise<void> {
    // If sync is already in progress, return the existing promise
    if (this.syncPromise) {

      return this.syncPromise;
    }

    if (!this.status.isOnline) {
      console.log('📱 Offline, skipping sync...');
      return;
    }

    // Create and store the sync promise
    this.syncPromise = this.performSyncInternal();

    try {
      await this.syncPromise;
    } finally {
      // Clear the promise when sync completes (success or failure)
      this.syncPromise = null;
    }
  }

  /**
   * Internal sync implementation
   */
  private async performSyncInternal(): Promise<void> {
    // Set syncing flag immediately to prevent race conditions
    this.status.isSyncing = true;

    const startTime = Date.now();

    try {

      this.emitEvent({ type: 'sync_started', data: { stage: 'initializing' } });

      // Phase 1: Upload local changes
      console.log('📤 Phase 1: Uploading local changes...');
      await this.uploadLocalChanges();
      console.log('✅ Phase 1 completed');

      // Phase 2: Download remote changes
      console.log('📥 Phase 2: Downloading remote changes...');
      await this.downloadRemoteChanges();
      console.log('✅ Phase 2 completed');

      // Phase 3: Process conflicts
      console.log('⚖️ Phase 3: Processing conflicts...');
      await this.processConflicts();
      console.log('✅ Phase 3 completed');

      // Update sync status
      console.log('📊 Updating sync status...');
      await this.loadSyncStatus();
      this.status.lastSyncTime = new Date().toISOString();

      const duration = Date.now() - startTime;
      console.log(`✅ Sync completed successfully in ${duration}ms`);

      // Emit the sync_completed event
      this.emitEvent({
        type: 'sync_completed',
        data: { duration, operations: this.status.pendingOperations }
      });

      // Sync completed successfully
    } catch (error) {
      console.error('❌ Sync failed:', error);
      this.emitEvent({
        type: 'sync_failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          stage: 'unknown'
        }
      });
      throw error;
    } finally {
      this.status.isSyncing = false;
      this.status.progress = null;
    }
  }

  /**
   * Upload local changes to remote
   */
  private async uploadLocalChanges(): Promise<void> {
    this.emitEvent({ type: 'sync_progress', data: { current: 1, total: 3, stage: 'uploading' } });

    const result = await syncOperations.uploadLocalChanges();
    console.log('📤 Upload result:', result);

    if (!result.success) {
      throw new Error(`Upload failed: ${result.errors.join(', ')}`);
    }

    // Upload completed
  }

  /**
   * Download remote changes to local
   */
  private async downloadRemoteChanges(): Promise<void> {
    this.emitEvent({ type: 'sync_progress', data: { current: 2, total: 3, stage: 'downloading' } });

    const result = await syncOperations.downloadRemoteChanges();
    console.log('📥 Download result:', result);

    if (!result.success) {
      throw new Error(`Download failed: ${result.errors.join(', ')}`);
    }

    // Download completed
  }

  /**
   * Process conflicts
   */
  private async processConflicts(): Promise<void> {
    this.emitEvent({ type: 'sync_progress', data: { current: 3, total: 3, stage: 'resolving conflicts' } });

    const conflicts = await conflictResolver.getUnresolvedConflicts();
    console.log('⚖️ Conflict resolution result:', { conflictCount: conflicts.length });

    if (conflicts.length > 0) {
      console.log(`⚖️ Found ${conflicts.length} unresolved conflicts`);

      // Try to resolve conflicts automatically
      let resolved = 0;
      for (const conflict of conflicts) {
        try {
          const conflictObj: Conflict = {
            id: conflict.id,
            tableName: conflict.table_name,
            recordId: conflict.record_id,
            conflictType: conflict.conflict_type as 'update_conflict' | 'delete_conflict' | 'create_conflict',
            localData: conflict.local_data,
            remoteData: conflict.remote_data,
            localTimestamp: (conflict.local_data?.updated_at as string) || (conflict.local_data?.created_at as string) || '',
            remoteTimestamp: (conflict.remote_data?.updated_at as string) || (conflict.remote_data?.created_at as string) || '',
            createdAt: conflict.created_at,
            resolved: false
          };

          const resolution = await conflictResolver.resolveConflict(conflictObj);
          if (resolution.resolved) {
            resolved++;
          }
        } catch (error) {
          console.error(`Failed to resolve conflict ${conflict.id}:`, error);
        }
      }

      console.log(`⚖️ Auto-resolved ${resolved}/${conflicts.length} conflicts`);
    }
  }

  /**
   * Perform initial data loading
   */
  async performInitialLoad(): Promise<void> {
    if (!this.status.isOnline) {
      throw new Error('Cannot perform initial load while offline');
    }

    // Starting initial data loading

    // Set up progress tracking
    selectiveLoader.setProgressCallback((progress) => {
      this.emitEvent({
        type: 'sync_progress',
        data: {
          current: progress.current,
          total: progress.total,
          stage: progress.stage
        }
      });
    });

    const result = await selectiveLoader.performInitialLoad();

    if (!result.success) {
      throw new Error(`Initial load failed: ${result.errors.join(', ')}`);
    }

    // Start lazy loading of technique images after initial data load
    this.startTechniqueImageCaching();

    // Initial load completed
  }

  /**
   * Start technique image caching in background
   */
  private startTechniqueImageCaching(): void {
    // Import and start technique image caching asynchronously
    // Don't await this to avoid blocking the initial load completion
    import('@/services/offline/assets/TechniqueImageManager')
      .then(({ techniqueImageManager }) => {
        techniqueImageManager.startLazyImageCaching().catch(error => {
          console.warn('⚠️ Technique image caching failed:', error);
        });
      })
      .catch(error => {
        console.warn('⚠️ Failed to import TechniqueImageManager:', error);
      });
  }

  /**
   * Force sync (manual trigger)
   */
  async forceSync(): Promise<void> {
    if (!this.status.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    // Force sync triggered
    await this.performSync();
  }

  /**
   * Get sync statistics
   */
  async getSyncStats(): Promise<{
    queueStats: { pending: number; processing: number; failed: number };
    conflictStats: { total: number; unresolved: number; resolved: number; byTable: Record<string, number> };
    lastSyncTimes: Record<string, string>;
  }> {
    const [queueStats, conflictStats, metadata] = await Promise.all([
      indexedDBService.syncQueue.getStats(),
      conflictResolver.getConflictStats(),
      indexedDBService.syncMetadata.getAllMetadata()
    ]);

    const lastSyncTimes: Record<string, string> = {};
    metadata.forEach(meta => {
      lastSyncTimes[meta.table_name] = meta.last_sync;
    });

    return {
      queueStats,
      conflictStats,
      lastSyncTimes
    };
  }

  /**
   * Clear sync data (for testing/reset)
   */
  async clearSyncData(): Promise<void> {
    await Promise.all([
      indexedDBService.syncQueue.clearProcessedItems(),
      conflictResolver.clearResolvedConflicts(),
      indexedDBService.offlineDB.clear('sync_metadata')
    ]);

    // Sync data cleared
  }

  /**
   * Cleanup and shutdown
   */
  async shutdown(): Promise<void> {
    this.stopBackgroundSync();
    this.eventListeners.clear();
    this.isInitialized = false;
    // Sync Service shut down
  }
}

// Export singleton instance
export const syncService = new SyncService();
